/**
 * VariableProximity
 *
 * Splits a string into individual character spans and adjusts each
 * character's font-weight based on its distance from the mouse cursor.
 * Requires a variable font loaded with a continuous wght axis (100..900).
 *
 * Props
 *  label       — the text to render
 *  className   — classes applied to the outer wrapper span
 *  charClass   — classes applied to every individual character span
 *  minWeight   — font-weight when far away   (default 200)
 *  maxWeight   — font-weight at cursor       (default 900)
 *  radius      — px distance at which effect starts to kick in (default 80)
 *  falloff     — curve exponent: higher = sharper fall-off (default 1.5)
 */

import { useRef, useEffect } from 'react';

interface VariableProximityProps {
  label: string;
  className?: string;
  charClass?: string;
  minWeight?: number;
  maxWeight?: number;
  radius?: number;
  falloff?: number;
}

export default function VariableProximity({
  label,
  className = '',
  charClass = '',
  minWeight = 200,
  maxWeight = 900,
  radius = 100,
  falloff = 1.8,
}: VariableProximityProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const charsRef = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleMouseMove = (e: MouseEvent) => {
      const chars = charsRef.current;
      if (!chars.length) return;

      for (const span of chars) {
        if (!span) continue;
        const rect = span.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
        const t = Math.max(0, 1 - (dist / radius) ** falloff);
        const weight = Math.round(minWeight + t * (maxWeight - minWeight));
        span.style.fontVariationSettings = `'wght' ${weight}`;
      }
    };

    const handleMouseLeave = () => {
      for (const span of charsRef.current) {
        if (span) span.style.fontVariationSettings = `'wght' ${maxWeight}`;
      }
    };

    // Attach to window so the effect works as soon as the cursor
    // enters the viewport area, not just when it's over the element.
    window.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [minWeight, maxWeight, radius, falloff]);

  // Split into chars, preserving spaces as non-breaking
  const chars = label.split('');

  return (
    <span ref={containerRef} className={className} aria-label={label}>
      {chars.map((char, i) => (
        <span
          key={i}
          ref={(el) => { charsRef.current[i] = el; }}
          className={charClass}
          style={{ fontVariationSettings: `'wght' ${maxWeight}`, display: 'inline-block' }}
          aria-hidden
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
}
